import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { STATUS_FLOW, STATUS_LABELS } from './statuses';
import './ApplicationDetail.css';

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');

  const fetchApplication = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/careers/${id}`);
      setApplication(response.data.application);
      setNotes(response.data.notes);
      setError('');
    } catch (err) {
      setError(err.response?.status === 404
        ? 'Application not found'
        : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  const handleStatusChange = async (status) => {
    try {
      setSaving(true);
      const response = await api.patch(`/careers/${id}/status`, { status });
      setApplication(prev => ({ ...prev, ...response.data.application }));
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleRating = async (rating) => {
    try {
      setSaving(true);
      const response = await api.patch(`/careers/${id}/rating`, { rating });
      setApplication(prev => ({ ...prev, ...response.data.application }));
    } catch (err) {
      setError('Failed to save rating');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      setSaving(true);
      await api.post(`/careers/${id}/notes`, { note: newNote.trim() });
      setNewNote('');
      // Refetch so the note carries its author name from the server
      const response = await api.get(`/careers/${id}`);
      setNotes(response.data.notes);
    } catch (err) {
      setError('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatYesNo = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '-';
  };

  const asList = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  if (loading) {
    return <div className="application-detail"><div className="loading">Loading application...</div></div>;
  }

  if (!application) {
    return (
      <div className="application-detail">
        <div className="error-message">{error || 'Application not found'}</div>
        <button className="btn-back" onClick={() => navigate('/careers')}>← Back to Applications</button>
      </div>
    );
  }

  const availability = asList(application.availability);
  const skills = asList(application.skills);

  return (
    <div className="application-detail">
      <button className="btn-back" onClick={() => navigate('/careers')}>← Back to Applications</button>

      {error && <div className="error-message">{error}</div>}

      <div className="detail-header">
        <div>
          <div className="detail-number">{application.application_number}</div>
          <h1>{application.first_name} {application.last_name}</h1>
          <p className="detail-subtitle">
            Applied for <strong>{application.position}</strong> on {formatDate(application.created_at)}
          </p>
        </div>
        <span className={`status-badge status-${application.status}`}>
          {STATUS_LABELS[application.status] || application.status}
        </span>
      </div>

      <div className="detail-actions">
        <div className="action-group">
          <label>Status</label>
          <div className="status-buttons">
            {STATUS_FLOW.map(status => (
              <button
                key={status}
                disabled={saving}
                className={`status-btn ${application.status === status ? 'active' : ''}`}
                onClick={() => handleStatusChange(status)}
              >
                {STATUS_LABELS[status]}
              </button>
            ))}
          </div>
        </div>

        <div className="action-group">
          <label>Rating</label>
          <div className="rating-stars">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                disabled={saving}
                className={`star ${application.rating >= star ? 'filled' : ''}`}
                onClick={() => handleRating(star)}
                aria-label={`Rate ${star} of 5`}
              >
                ★
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h2>Contact</h2>
          <div className="field-row">
            <span className="field-label">Email</span>
            <span className="field-value"><a href={`mailto:${application.email}`}>{application.email}</a></span>
          </div>
          <div className="field-row">
            <span className="field-label">Phone</span>
            <span className="field-value"><a href={`tel:${application.phone}`}>{application.phone}</a></span>
          </div>
          <div className="field-row">
            <span className="field-label">Location</span>
            <span className="field-value">
              {[application.city, application.state].filter(Boolean).join(', ') || '-'}
            </span>
          </div>
          <div className="field-row">
            <span className="field-label">18 or older</span>
            <span className="field-value">{formatYesNo(application.is_adult)}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Authorized to work in the US</span>
            <span className="field-value">{formatYesNo(application.authorized_to_work)}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Heard about us via</span>
            <span className="field-value">{application.heard_about_us || '-'}</span>
          </div>
        </div>

        <div className="detail-card">
          <h2>Position & Availability</h2>
          <div className="field-row">
            <span className="field-label">Position</span>
            <span className="field-value">{application.position}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Employment type</span>
            <span className="field-value">{application.employment_type || '-'}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Desired hours</span>
            <span className="field-value">{application.desired_hours || '-'}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Earliest start</span>
            <span className="field-value">{formatDate(application.earliest_start_date)}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Days available</span>
            <span className="field-value">
              {availability.length ? (
                <div className="chip-list">
                  {availability.map(day => <span key={day} className="chip">{day}</span>)}
                </div>
              ) : '-'}
            </span>
          </div>
          {application.availability_notes && (
            <div className="field-block">
              <span className="field-label">Availability notes</span>
              <p>{application.availability_notes}</p>
            </div>
          )}
        </div>

        <div className="detail-card">
          <h2>Experience</h2>
          <div className="field-row">
            <span className="field-label">Currently employed</span>
            <span className="field-value">{formatYesNo(application.currently_employed)}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Retail experience</span>
            <span className="field-value">{application.retail_experience || '-'}</span>
          </div>
          <div className="field-row">
            <span className="field-label">Skills</span>
            <span className="field-value">
              {skills.length ? (
                <div className="chip-list">
                  {skills.map(skill => <span key={skill} className="chip">{skill}</span>)}
                </div>
              ) : '-'}
            </span>
          </div>
          {application.work_history && (
            <div className="field-block">
              <span className="field-label">Work history</span>
              <p>{application.work_history}</p>
            </div>
          )}
          {application.education && (
            <div className="field-block">
              <span className="field-label">Education</span>
              <p>{application.education}</p>
            </div>
          )}
        </div>

        <div className="detail-card">
          <h2>In Their Words</h2>
          {application.why_interested ? (
            <div className="field-block">
              <span className="field-label">Why Fitness First Sports</span>
              <p>{application.why_interested}</p>
            </div>
          ) : <p className="muted">No response given.</p>}
          {application.references_text && (
            <div className="field-block">
              <span className="field-label">References</span>
              <p>{application.references_text}</p>
            </div>
          )}
          {application.signature_name && (
            <div className="field-row">
              <span className="field-label">Signed</span>
              <span className="field-value signature">{application.signature_name}</span>
            </div>
          )}
        </div>
      </div>

      <div className="detail-card notes-card">
        <h2>Staff Notes</h2>

        <form onSubmit={handleAddNote} className="note-form">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note about this applicant (interview scheduled, called, etc.)"
            rows={3}
          />
          <button type="submit" disabled={saving || !newNote.trim()}>
            {saving ? 'Saving...' : 'Add Note'}
          </button>
        </form>

        {notes.length === 0 ? (
          <p className="muted">No notes yet.</p>
        ) : (
          <ul className="notes-list">
            {notes.map(note => (
              <li key={note.id}>
                <div className="note-meta">
                  <strong>{note.author_name || 'Unknown'}</strong>
                  <span>{formatDateTime(note.created_at)}</span>
                </div>
                <p>{note.note}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {application.reviewed_by_name && (
        <p className="review-footer">
          Last updated by {application.reviewed_by_name} on {formatDateTime(application.reviewed_at)}
        </p>
      )}
    </div>
  );
};

export default ApplicationDetail;
